import Vue from "vue"
import Notifications from "@/managers/session/notifications"
import Portal from "@/managers/portal"
import {watch, when} from "@/store/store"
import Tracking from "@/managers/session/logging/tracking"
import RefreshToken from "@/managers/authentication/refreshToken"
import UsernamePassword from "@/managers/authentication/usernamePassword"
import StashedAccessToken from "@/managers/authentication/stashedAccessToken"
import Passkey from "@/managers/authentication/passkey"
import JwtAuthenticationMethod from "@/managers/authentication/jwtAuthenticationMethod"

class Authentication {
	private static readonly lockKey = "autoLogin"

	private readonly state = Vue.observable({isCheckingLogin: true})
	private expiredTokenHandlingPromise: Promise<boolean> | null = null

	public get isLoggingIn(): boolean {
		return this.isCheckingLogin || UsernamePassword.isLoggingIn || RefreshToken.isLoggingIn || StashedAccessToken.isLoggingIn || Passkey.isLoggingIn
	}

	public get isAuthenticated(): boolean {
		return Portal.hasAccessToken && Portal.accessToken!.isMultifactorSatisfied
	}

	public get isMultiFactorLoginRequired(): boolean {
		return Portal.hasAccessToken && !Portal.accessToken!.isMultifactorSatisfied
	}

	public get canLogin(): boolean {
		return !this.isAuthenticated && !this.isLoggingIn
	}

	public get authenticationType(): string | null {
		return Portal.client.authenticationType
	}

	private get isCheckingLogin(): boolean {
		return this.state.isCheckingLogin
	}

	private get canAutoLogin(): boolean {
		return StashedAccessToken.canLogin || RefreshToken.canLogin
	}

	public constructor() {
		watch(
			() => Portal.authenticationType,
			authenticationType => {
				if (authenticationType !== null)
					Tracking.login(authenticationType)
			},
			true
		)

		watch(
			() => this.canLogin && this.canAutoLogin && !this.isCheckingLogin,
			shouldTry => {
				if (shouldTry) {
					Notifications.debug("Triggering auto login based on state")
					this.attemptAutoLogin()
						.then(result => Notifications.debug("Triggered auto login based on state result: " + result))
				}
			}
		)

		setTimeout(() => { // Prevents problems with circular dependencies on tracking / logging
			this.attemptAutoLogin()
				.then(result => Notifications.debug("Triggered initial auto login result: " + result))
				.finally(() => {
					this.state.isCheckingLogin = false
					this.handleAccessTokenExpired()
				})
		})

		StashedAccessToken.addEventListener(
			JwtAuthenticationMethod.expiredEvent,
			() => {
				Notifications.debug("Stashed access token expired")

				if (Portal.hasAccessToken) {
					const token = Portal.accessToken!

					if (!token.calculateIsExpiredNow())
						return

					this.attemptAutoLogin()
						.then(result => {
							Notifications.debug("Triggered auto login based on expired token result: " + result)

							if (!result && Portal.hasAccessToken && Portal.accessToken!.raw === token.raw) {
								Notifications.debug("Clearing expired token")
								Portal.clearSession()
							}
						})
				}
			}
		)
	}

	public async logOut(): Promise<void> {
		if (this.isLoggingIn)
			throw new Error("Can't log out when currently logging in")

		if (!this.isAuthenticated)
			return

		await StashedAccessToken.clearAndDelete()
		await RefreshToken.clearAndDelete()
		Portal.clearSession()
		Tracking.event("Authentication", "Log out")
	}

	public watchIsAuthenticated(callback: (isAuthenticated: boolean) => void, immediate: boolean = false): () => void {
		return watch(() => this.isAuthenticated, callback, immediate)
	}

	public async checkLogin(): Promise<boolean> {
		await when(() => !this.isLoggingIn)

		return this.isAuthenticated
	}

	public async shouldLoginWithPasskey(email: string): Promise<boolean> {
		const options = await Portal.call.auth.optionsGet(email).response

		return options.IsPasskeyRequired
	}

	public async attemptAutoLogin(): Promise<boolean> {
		Notifications?.debug("Attempting auto login")
		const call = async () => {
			try {
				if (StashedAccessToken.canLogin) {
					await StashedAccessToken.login()
					return true
				} else if (StashedAccessToken.loginPromise !== null) {
					await StashedAccessToken.loginPromise
					return true
				}
			} catch (error: any) {
				Notifications?.warning(`Failed to use StashedAccessToken login: ${error.message}`)
			}
			try {
				if (RefreshToken.canLogin) {
					await RefreshToken.login()
					return true
				} else if (RefreshToken.loginPromise !== null) {
					await RefreshToken.loginPromise
					return true
				}
			} catch (error: any) {
				Notifications?.warning(`Failed to use RefreshToken login: ${error.message}`)
			}
			return false
		}

		if (navigator.locks !== undefined)
			return navigator.locks.request(Authentication.lockKey, call)
		else
			return call()
	}

	private handleAccessTokenExpired(): void {
		Portal.accessTokenExpiredHandler = async token => {
			if (token !== null) {
				await StashedAccessToken.clearInvalidToken(token)
				await RefreshToken.clearInvalidToken(token)
			}

			return this.startHandlingExpiredToken(token)
		}
	}

	private startHandlingExpiredToken(token: string | null): Promise<boolean> {
		if (this.expiredTokenHandlingPromise === null) {
			Notifications?.debug("Handling expired token")
			this.expiredTokenHandlingPromise = this.attemptAutoLogin()
				.catch(
					reason => {
						Notifications?.warning("Failed to reauthenticate after expired session", reason)
						return false
					}
				)
				.then(success => {
					const currentToken = Portal.accessToken

					if (!success && currentToken !== null && currentToken.raw === token)
						Portal.clearSession()

					return Portal.hasAccessToken
				})
				.finally(() => this.expiredTokenHandlingPromise = null)
		}

		return this.expiredTokenHandlingPromise
	}
}

export default new Authentication()
